import { useState } from 'react';
import type { FieldConfig } from '../../lib/fieldValue';

interface FieldConfigEditorProps {
  config: FieldConfig;
  onChange: (config: FieldConfig) => void;
  disabled?: boolean;
}

const inputClass =
  'min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text disabled:cursor-not-allowed disabled:opacity-50';

function parseList(raw: string): string[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/**
 * Type-specific config inputs for one field, per `docs/specs/WP10-template-editor.md`: tiers as a
 * comma list, stars max + optional labels, duration max seconds, ratio labels, number min/max/step,
 * text max length, nothing for image/link. The caller keys this on the field's `type` (see
 * `FieldRow`) so a type change remounts it fresh rather than needing to reconcile stale local text
 * state against a config shape that no longer matches.
 */
export function FieldConfigEditor({ config, onChange, disabled = false }: FieldConfigEditorProps) {
  switch (config.type) {
    case 'TIER':
      return <TierConfigEditor config={config} onChange={onChange} disabled={disabled} />;
    case 'STARS':
      return <StarsConfigEditor config={config} onChange={onChange} disabled={disabled} />;
    case 'DURATION':
      return (
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">Max seconds</span>
          <input
            type="number"
            min={1}
            max={86400}
            value={config.maxSeconds}
            disabled={disabled}
            onChange={(event) => onChange({ type: 'DURATION', maxSeconds: Number(event.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case 'RATIO':
      return (
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Left label</span>
            <input
              type="text"
              value={config.leftLabel}
              disabled={disabled}
              onChange={(event) => onChange({ ...config, leftLabel: event.target.value })}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Right label</span>
            <input
              type="text"
              value={config.rightLabel}
              disabled={disabled}
              onChange={(event) => onChange({ ...config, rightLabel: event.target.value })}
              className={inputClass}
            />
          </label>
        </div>
      );
    case 'NUMBER':
      return (
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Min</span>
            <input
              type="text"
              inputMode="decimal"
              value={config.min ?? ''}
              disabled={disabled}
              onChange={(event) => onChange({ ...config, min: event.target.value === '' ? null : event.target.value })}
              className={`telemetry ${inputClass}`}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Max</span>
            <input
              type="text"
              inputMode="decimal"
              value={config.max ?? ''}
              disabled={disabled}
              onChange={(event) => onChange({ ...config, max: event.target.value === '' ? null : event.target.value })}
              className={`telemetry ${inputClass}`}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Step</span>
            <input
              type="text"
              inputMode="decimal"
              value={config.step ?? ''}
              disabled={disabled}
              onChange={(event) => onChange({ ...config, step: event.target.value === '' ? null : event.target.value })}
              className={`telemetry ${inputClass}`}
            />
          </label>
        </div>
      );
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
      return (
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">Max length</span>
          <input
            type="number"
            min={1}
            max={config.type === 'SHORT_TEXT' ? 200 : 10000}
            value={config.maxLength}
            disabled={disabled}
            onChange={(event) => onChange({ ...config, maxLength: Number(event.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case 'IMAGE':
    case 'LINK':
      return <p className="telemetry text-text-faint">No configuration for this type.</p>;
  }
}

function TierConfigEditor({
  config,
  onChange,
  disabled,
}: {
  config: Extract<FieldConfig, { type: 'TIER' }>;
  onChange: (config: FieldConfig) => void;
  disabled: boolean;
}) {
  const [raw, setRaw] = useState(config.tiers.join(', '));
  return (
    <label className="flex flex-col gap-1 text-sm text-text-muted">
      <span className="eyebrow">Tiers (best first)</span>
      <input
        type="text"
        value={raw}
        disabled={disabled}
        placeholder="SSS, S, A, B, C, D"
        onChange={(event) => {
          setRaw(event.target.value);
          onChange({ type: 'TIER', tiers: parseList(event.target.value) });
        }}
        className={`telemetry ${inputClass}`}
      />
    </label>
  );
}

function StarsConfigEditor({
  config,
  onChange,
  disabled,
}: {
  config: Extract<FieldConfig, { type: 'STARS' }>;
  onChange: (config: FieldConfig) => void;
  disabled: boolean;
}) {
  const [rawLabels, setRawLabels] = useState(config.labels.join(', '));

  function setMax(max: number) {
    // Labels must stay exactly max+1 entries — pad/truncate rather than leaving a stale array the
    // server would reject.
    const labels = Array.from({ length: max + 1 }, (_, i) => config.labels[i] ?? '');
    setRawLabels(labels.join(', '));
    onChange({ type: 'STARS', max, labels });
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[8rem_1fr]">
      <label className="flex flex-col gap-1 text-sm text-text-muted">
        <span className="eyebrow">Max stars</span>
        <input
          type="number"
          min={1}
          max={10}
          value={config.max}
          disabled={disabled}
          onChange={(event) => setMax(Number(event.target.value))}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-text-muted">
        <span className="eyebrow">Labels (0..max, e.g. F..SSS)</span>
        <input
          type="text"
          value={rawLabels}
          disabled={disabled}
          onChange={(event) => {
            setRawLabels(event.target.value);
            onChange({ type: 'STARS', max: config.max, labels: parseList(event.target.value) });
          }}
          className={`telemetry ${inputClass}`}
        />
      </label>
    </div>
  );
}
